import { useEffect, useRef, useState } from "react";
import { Field } from "../../components/ui";
import {
  suggestAddresses, addressLookupConfigured, lastLookupError,
  type AddressParts, type Suggestion,
} from "../../lib/addressLookup";

/**
 * The street field, with suggestions underneath.
 *
 * Behaves as an ordinary text field when Google is not configured or cannot
 * be reached — the suggestions are an accelerator, never a gate. Somebody
 * typing an address on a bad connection still gets to finish.
 */
export default function AddressField({
  value, onChange, onPick, error, onBlur,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Called when a suggestion is chosen, with city, state, ZIP and county. */
  onPick: (parts: AddressParts) => void;
  error?: string | null;
  onBlur?: () => void;
}) {
  const [items, setItems] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState<string | null>(null);
  const typed = useRef(0);

  useEffect(() => {
    if (!addressLookupConfigured() || !open) return;
    const q = value;
    if (q.trim().length < 4) { setItems([]); return; }

    /* Wait for a pause in typing. Firing on every keystroke would be both
       slower to read and, without the session token, expensive. */
    const seq = ++typed.current;
    const t = setTimeout(async () => {
      setBusy(true);
      const found = await suggestAddresses(q);
      if (seq === typed.current) { setItems(found); setWhy(lastLookupError); }
      setBusy(false);
    }, 250);
    return () => clearTimeout(t);
  }, [value, open]);

  async function choose(s: Suggestion) {
    setOpen(false);
    setItems([]);
    const parts = await s.resolve();
    if (parts.street) onChange(parts.street);
    onPick(parts);
  }

  return (
    <div className="addrwrap">
      <Field
        label="Street" value={value} error={error}
        /* Not "street-address". That token is what tells Chrome to offer its
           own saved addresses, and its dropdown lands on top of ours. An
           unrecognised token leaves the browser with nothing to match on, so
           only our suggestions appear. The password managers are told to keep
           out for the same reason. */
        autoComplete="address-search"
        data-1p-ignore data-lpignore="true" data-form-type="other"
        onChange={(v) => { onChange(v); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Late enough for a click on a suggestion to land first.
          setTimeout(() => setOpen(false), 150);
          onBlur?.();
        }}
      />
      {open && (items.length > 0 || busy || why) && (
        <ul className="addrlist" role="listbox">
          {busy && items.length === 0 && <li className="addr-busy">Looking</li>}
          {!busy && why && <li className="addr-busy addr-why">{why}</li>}
          {!busy && !why && items.length === 0 && value.trim().length >= 4 && (
            <li className="addr-busy">No match — keep typing, or enter it by hand</li>
          )}
          {items.map((s) => (
            <li key={s.label}>
              <button type="button" className="addr-item"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => void choose(s)}>
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
